import React from 'react';
import { Link } from 'react-router-dom';
import MagicBento, { type MagicBentoProps } from './MagicBento';

export interface MagicButtonProps extends Omit<MagicBentoProps, 'variant'> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'glass';
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  fullWidth?: boolean;
  to?: string;
}

const MagicButton: React.FC<MagicButtonProps> = ({
  children,
  variant = 'primary',
  icon,
  iconPosition = 'left',
  loading = false,
  fullWidth = false,
  className = '',
  to,
  ...props
}) => {
  const variantMap = {
    primary: 'button',
    secondary: 'glass',
    outline: 'outline',
    ghost: 'default',
    glass: 'glass'
  } as const;

  const additionalClasses = {
    primary: '',
    secondary: 'bg-neutral-white/10 hover:bg-neutral-white/20 text-neutral-white border-neutral-white/20 hover:border-primary-blue/50',
    outline: '',
    ghost: 'bg-transparent hover:bg-neutral-white/10 text-neutral-white border-transparent hover:border-neutral-white/20',
    glass: 'text-neutral-white hover:bg-neutral-white/15'
  };

  const widthClass = fullWidth ? 'w-full' : '';
  const combinedClassName = `${additionalClasses[variant]} ${widthClass} ${className}`;

  const content = (
    <div className="flex items-center justify-center gap-2 relative z-10">
      {loading && (
        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      )}
      {icon && iconPosition === 'left' && !loading && (
        <span className="flex-shrink-0">{icon}</span>
      )}
      <span className="font-medium">{children}</span>
      {icon && iconPosition === 'right' && !loading && (
        <span className="flex-shrink-0">{icon}</span>
      )}
    </div>
  );

  if (to) {
    return (
      <Link to={to} className={combinedClassName} {...props}>
        <MagicBento
          variant={variantMap[variant]}
          className="w-full h-full"
          glowEffect={variant === 'primary'}
          shimmerEffect={variant === 'primary' || variant === 'outline'}
        >
          {content}
        </MagicBento>
      </Link>
    );
  }

  return (
    <MagicBento
      variant={variantMap[variant]}
      className={combinedClassName}
      glowEffect={variant === 'primary'}
      shimmerEffect={variant === 'primary' || variant === 'outline'}
      {...props}
    >
      {content}
    </MagicBento>
  );
};

export default MagicButton;